import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
    setInvalidationField,
    setInvalidationForm,
    resetInvalidationForm,
    setInvalidationFormErrors,
    clearInvalidationFormErrors,
} from '../../../store/slices/purchases/purchaseInvalidationSlice';

import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../store/store';
import useLoading from '../../useLoading';
import useApiErrorHandler from '../../useApiErrorHandler';
import useConfirmModal from '../../../hooks/useConfirmModal';
import useModals from '../../useModals';
import { PurchaseInvalidationDTO } from '../../../types/purchases/invalidation/PurchaseInvalidationDTO';
import PurchaseInvalidationService from '../../../services/purchases/PurchaseInvalidationService';
import { mapPrefillToForm, toCreatePayload } from '../../../mappers/purchase/invalidation/purchaseInvalidationMapper';
import { PurchaseSummary } from '../../../types/purchases/BasePurchase';
import { useBasePurchase } from '../useBasePurchase';

export const usePurchaseInvalidation = () => {
    const dispatch: AppDispatch = useDispatch();

    const {
        form,
        isSubmitting,
        formErrors,
    } = useSelector((state: RootState) => state.purchaseInvalidation);

    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handlerClearFilteredPurchaseList } = useBasePurchase();
    const { handle } = useApiErrorHandler();
    const navigate = useNavigate();
    const confirm = useConfirmModal();
    const { openModal } = useModals();

    const submitInvalidation = () => {
        confirm({
            title: 'Confirmar anulación de compra',
            message: 'Se notificará a Hacienda que el DTE de la compra será invalidado. ¿Desea continuar?',
            confirmText: 'Anular compra',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const payload = toCreatePayload(form);
                    console.log(payload);
                    const response = await PurchaseInvalidationService.register(payload);
                    const { statusCode, type, title, message } = response;
                    if (statusCode === 200) {
                        openModal(type, { title, message });
                        dispatch(resetInvalidationForm());
                    }
                    handlerClearFilteredPurchaseList();
                } catch (e) {
                    handle(e);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    const loadPrefillFromPurchase = async (purchase: PurchaseSummary) => {
        try {
            showLoadingIndicator();
            const response = await PurchaseInvalidationService.getPrefill(purchase.id);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                const mapped = mapPrefillToForm(data);
                dispatch(setInvalidationForm(mapped));
                navigate('/purchase-cancel');
                handlerClearFilteredPurchaseList();
            }
        } catch (e) {
            handle(e);
        } finally {
            hideLoadingIndicator();
        }
    };

    const resetForm = () => {
        dispatch(resetInvalidationForm());
    };

    const onChangeInvalidationType = useCallback((raw: string | number) => {
        const value = (raw ?? '').toString() as '1' | '2' | '3' | '';
        dispatch(setInvalidationField({ field: 'invalidationTypeCode', value: value || null }));
        if (value === '2') {
            dispatch(setInvalidationField({ field: 'replacementGenerationCode', value: null }));
        }
    }, [dispatch]);

    const updateField = useCallback(
        <K extends keyof PurchaseInvalidationDTO>(field: K, value: PurchaseInvalidationDTO[K]) => {
            dispatch(setInvalidationField({ field, value }));
        },
        [dispatch]
    );

    return {
        form,
        isSubmitting,
        formErrors,
        updateField,
        submitInvalidation,
        resetForm,
        loadPrefillFromPurchase,
        setErrors: (errors: Record<string, string>) => dispatch(setInvalidationFormErrors(errors)),
        clearErrors: () => dispatch(clearInvalidationFormErrors()),

        onChangeInvalidationType,
    };
};
